import { useCallback, useMemo, useState } from 'react'
import { writeTaxonomy } from '../data/settings'
import { MAX_GRID, MIN_GRID, normaliseLayout, pruneLayout } from '../data/layout'
import { newFlagId, taxonomyProblem, toStoredTaxonomy, toggleFlag } from '../data/taxonomy'
import { LayoutEditor } from './LayoutEditor'
import { FlagsEditor } from './FlagsEditor'
import { SaveStatus } from './SaveStatus'
import { useAutoSave } from './useAutoSave'
import { sameValue } from '../lib/equal'
import type { Category, GridLayout, Taxonomy } from '../data/types'

/**
 * Ids are stable and never reused, so they are derived from the label once, at
 * creation, and left alone through every later rename.
 */
export function newCategoryId(label: string, taken: Set<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 24) || 'category'
  if (!taken.has(base)) return base
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`
    if (!taken.has(candidate)) return candidate
  }
}

interface Props {
  uid: string
  taxonomy: Taxonomy
}

export function TaxonomyEditor({ uid, taxonomy }: Props) {
  const [draft, setDraft] = useState<Taxonomy>(() => ({
    ...taxonomy,
    layout: normaliseLayout(taxonomy.layout),
  }))

  const normalised = useMemo(
    () => ({ ...taxonomy, layout: normaliseLayout(taxonomy.layout) }),
    [taxonomy],
  )
  const layout = draft.layout as GridLayout

  /**
   * Exactly what a write would store. Comparing the projections rather than the
   * drafts is what lets a landed write settle: the snapshot comes back through
   * normalisation, so anything it drops or reorders would otherwise leave the
   * editor permanently "unsaved".
   */
  const writable = (next: Taxonomy) =>
    toStoredTaxonomy({ ...next, layout: pruneLayout(next.layout as GridLayout, next.categories) })

  const dirty = useMemo(
    () => !sameValue(writable(draft), writable(normalised)),
    [draft, normalised],
  )

  const write = useCallback(
    (next: Taxonomy) =>
      writeTaxonomy(uid, {
        ...next,
        layout: pruneLayout(next.layout as GridLayout, next.categories),
      }),
    [uid],
  )
  const status = useAutoSave(draft, dirty, write, { validate: taxonomyProblem })

  function patch(index: number, change: Partial<Category>) {
    setDraft((current) => ({
      ...current,
      categories: current.categories.map((category, i) =>
        i === index ? { ...category, ...change } : category,
      ),
    }))
  }

  /**
   * Retiring a category takes it off the grid in the same step. The write prunes
   * the layout anyway, so leaving the cell in the draft would only show a dead
   * button, labelled with a raw id, until the page was reloaded.
   */
  function toggleArchived(index: number) {
    setDraft((current) => {
      const categories = current.categories.map((category, i) =>
        i === index ? { ...category, archived: !category.archived } : category,
      )
      return {
        ...current,
        categories,
        layout: pruneLayout(current.layout as GridLayout, categories),
      }
    })
  }

  function move(index: number, delta: number) {
    setDraft((current) => {
      const next = [...current.categories]
      const target = index + delta
      if (target < 0 || target >= next.length) return current
      const moved = next[index] as Category
      next[index] = next[target] as Category
      next[target] = moved
      return { ...current, categories: next }
    })
  }

  function add() {
    setDraft((current) => {
      const taken = new Set(current.categories.map((category) => category.id))
      const id = newCategoryId('new category', taken)
      return {
        ...current,
        categories: [
          ...current.categories,
          {
            id,
            label: 'New category',
            shortLabel: 'new',
            color: '#7c8798',
            flags: [],
            archived: false,
            excludeFromInsights: false,
          },
        ],
      }
    })
  }

  return (
    <div>
      <p className="px-3 pt-3 text-[12px] leading-relaxed text-faint">
        The first field is the full name, the second is what fits on a grid button. The arrows
        set the order categories appear in pickers; the bin retires one from the grid and the
        pickers without touching the history that points at it, whose time goes on counting.
        Clearing <strong className="font-medium text-dim">Count in statistics</strong> is the
        separate thing, and the one lunch wants: it keeps that category's time out of every
        figure — the tracked totals on Today and Week as well as Insights and Iterations. Where
        the buttons sit on the Now grid is set under Grid layout below. Changes save themselves
        a moment after you stop typing.
      </p>
      <ul>
        {draft.categories.map((category, index) => (
          <li key={category.id} className="border-b border-line px-3 py-2.5 last:border-0">
            <div className="flex items-center gap-2">
              <label className="relative size-6 shrink-0 overflow-hidden rounded-md border border-line-strong">
                <span className="absolute inset-0" style={{ backgroundColor: category.color }} />
                <input
                  type="color"
                  value={category.color}
                  onChange={(event) => patch(index, { color: event.target.value })}
                  aria-label={`Colour for ${category.label}`}
                  className="absolute inset-0 cursor-pointer opacity-0"
                />
              </label>

              <input
                value={category.label}
                onChange={(event) => patch(index, { label: event.target.value })}
                aria-label="Name"
                placeholder="Name"
                className="min-w-0 flex-1 rounded-md border border-line bg-bg px-2 py-1 text-[14px] outline-none focus:border-line-strong"
              />
              <input
                value={category.shortLabel}
                onChange={(event) => patch(index, { shortLabel: event.target.value })}
                aria-label="Button label"
                placeholder="button"
                title="What fits on a grid button"
                className="w-20 shrink-0 rounded-md border border-line bg-bg px-2 py-1 text-[14px] outline-none focus:border-line-strong"
              />

              <div className="flex shrink-0 items-center">
                <IconButton label="Move up" onClick={() => move(index, -1)} disabled={index === 0}>
                  <path d="M6 14l6-6 6 6" />
                </IconButton>
                <IconButton
                  label="Move down"
                  onClick={() => move(index, 1)}
                  disabled={index === draft.categories.length - 1}
                >
                  <path d="M6 10l6 6 6-6" />
                </IconButton>
                <IconButton
                  label={
                    category.archived
                      ? 'Restore to the grid and the pickers'
                      : 'Retire from the grid and the pickers — its history still counts'
                  }
                  onClick={() => toggleArchived(index)}
                  active={category.archived}
                >
                  <path d="M4 7h16v3H4z" />
                  <path d="M6 10v9h12v-9" />
                  <path d="M10 14h4" />
                </IconButton>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2 pl-8">
              {/* A property of the category itself, so it is a labelled checkbox
                  rather than another chip in the flag row. */}
              <label
                className="flex shrink-0 items-center gap-1.5 text-[12px] text-dim select-none"
                title="Uncheck to leave this category's time out of every figure — the tracked totals on Today and Week as well as Insights and Iterations"
              >
                <input
                  type="checkbox"
                  checked={!category.excludeFromInsights}
                  onChange={(event) =>
                    patch(index, { excludeFromInsights: !event.target.checked })
                  }
                  className="size-3.5 accent-[var(--tg-accent)]"
                />
                Count in statistics
              </label>

              <span className="flex flex-wrap items-center gap-1.5">
                <span className="pr-0.5 text-[11px] tracking-wide text-faint uppercase">
                  Flags
                </span>
                {draft.flags.length === 0 ? (
                  <span className="text-[11px] text-faint">none yet — add some below</span>
                ) : null}
                {draft.flags.map((flag) => (
                  <Chip
                    key={flag.id}
                    active={category.flags.includes(flag.id)}
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        categories: current.categories.map((candidate, i) =>
                          i === index ? toggleFlag(candidate, flag.id) : candidate,
                        ),
                      }))
                    }
                  >
                    {flag.label}
                  </Chip>
                ))}
              </span>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-3 border-t border-line px-3 py-3">
        <button
          type="button"
          onClick={add}
          className="rounded-lg border border-line-strong px-3 py-1.5 text-[14px] font-medium text-dim transition-colors duration-150 hover:text-text"
        >
          Add category
        </button>
        <span className="ml-auto text-right" role="status" aria-live="polite">
          <SaveStatus state={status.state} message={status.message} />
        </span>
      </div>

      <div className="border-t border-line px-3 py-3">
        <h3 className="pb-1 text-[12px] font-semibold tracking-wide text-faint uppercase">Flags</h3>
        <p className="pb-3 text-[12px] leading-relaxed text-faint">
          Your own labels, for whatever cuts you care about — dev, off-work, on-call. A category
          can carry as many as you like, and Insights can group and filter by them.
        </p>
        <FlagsEditor
          flags={draft.flags}
          nextId={(label) => newFlagId(label, new Set(draft.flags.map((flag) => flag.id)))}
          onChange={(flags) =>
            setDraft((current) => {
              const ids = new Set(flags.map((flag) => flag.id))
              return {
                ...current,
                flags,
                categories: current.categories.map((category) => ({
                  ...category,
                  flags: category.flags.filter((id) => ids.has(id)),
                })),
              }
            })
          }
        />
      </div>

      <div className="border-t border-line px-3 py-3">
        <h3 className="pb-1 text-[12px] font-semibold tracking-wide text-faint uppercase">Grid layout</h3>
        <p className="pb-3 text-[12px] leading-relaxed text-faint">
          The shape of the Now grid. Between {MIN_GRID} and {MAX_GRID} columns and rows, buttons
          merged into bigger targets, split into two stacked halves, and dragged into the order
          you want.
        </p>
        <LayoutEditor
          layout={layout}
          categories={draft.categories.filter((category) => !category.archived)}
          onChange={(next) => setDraft((current) => ({ ...current, layout: next }))}
        />
      </div>
    </div>
  )
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded border px-1.5 py-0.5 text-[11px] font-medium tracking-wide uppercase transition-colors duration-150 ${
        active ? 'border-line-strong bg-raised text-text' : 'border-line text-faint hover:text-dim'
      }`}
    >
      {children}
    </button>
  )
}

function IconButton({
  label,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`flex size-6 items-center justify-center rounded transition-colors duration-150 hover:bg-raised hover:text-text disabled:pointer-events-none disabled:opacity-25 ${
        active ? 'bg-raised text-text' : 'text-dim'
      }`}
    >
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {children}
      </svg>
    </button>
  )
}
